import React, { useState, useLayoutEffect, useMemo } from 'react';
import { Workflow } from 'components/common';
import { useTheme } from '@mui/material/styles';
import { Box, Typography, IconButton, Divider, Button, Snackbar, Backdrop, CircularProgress } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useTemplate } from 'components/hooks';
import AddOutlinedIcon from '@mui/icons-material/AddOutlined';
import RemoveOutlinedIcon from '@mui/icons-material/RemoveOutlined';
import ConnectionComponent from './ConnectionComponent';
import AddTaskComponent from './AddTaskComponent';
import EditTaskComponent from './EditTaskComponent';
import LinkComponent from './LinkComponent';


export default function WorkflowForm({ wfParam=[], mode, projectId, readonly = false, handleCancelSubmit, handleConfirmSubmit }) {
  const theme = useTheme();
  const { getTemplateByProject, isLoaded, data } = useTemplate();
  const [t] = useTranslation();
  const [scale, setScale] = useState(1.3);
  const [taskSelected, setTaskSelected] = useState(null);
  const [tab, setTab] = useState('empty');
  const [nodes, setNodes] = useState([...wfParam]);
  const [hashMapNodes, setHashMapNodes] = useState();
  const [updateWF, setUpdateWF] = useState(0);
  const [counterNode, setCounterNode] = useState();
  const initialState = {
    active: false,
    selected: null,
    nodes: {}
  };
  const [linkMode, setLinkMode] = useState(initialState);

  const optionsAction = [
    { value: 'succed', label: t('common.nodesOption.succed') },
    { value: 'error', label: t('common.nodesOption.error') },
    { value: 'always', label: t('common.nodesOption.always') },
    { value: 'condition', label: t('common.nodesOption.condition') },
    { value: 'no-dependency', label: t('common.nodesOption.noDependency') }
  ];

  function createHashMapNodes() {
    const hashMap = nodes.reduce((acum, column, columnIndex) => {
      const columnsValues = column.reduce((acumTask, task, rowIndex) => {
        return {
          ...acumTask,
          [`${task.id_nodo}`]: { column: columnIndex, row: rowIndex }
        }
      }, {});

      return { ...acum, ...columnsValues };
    }, {});

    setHashMapNodes(hashMap);
  }

  function handleZoomOut() {
    return setScale(scale - 0.1);
  }

  function handleZoomIn() {
    return setScale(scale + 0.1);
  }

  function findIndexNodeById(id) {
    return hashMapNodes[`${id}`];
  }

  function getCounterNode() {
    let counter = 0;

    if (nodes?.length <= 0) return counter;

    nodes.forEach((value) => {
      counter += value.length;
    });

    return counter;
  }

  function getMaxIdNode() {
    const result = nodes.reduce((acum, column) => {
      const maxRow = column.reduce((a, b) => Math.max(a, b.id_nodo), 0);
      if (maxRow > acum) return maxRow;
      else return acum
    }, 0);

    return result;
  }

  function handleUpdateWf() {
    createHashMapNodes();
    setUpdateWF(updateWF+1);
  }

  function handleDelete(task) {
    const index = findIndexNodeById(task.id_nodo);
    nodes[index.column].splice(index.row, 1);

    if (nodes[index.column] <= 0) nodes.splice(index.column, 1);

    setNodes(nodes);
    handleUpdateWf();
    handleCancel();
  }

  function handleDeleteConnetion(task) {
    const { job, con } = task;
    const idToFilter = con.id_nodo;
    const index = findIndexNodeById(job.id_nodo);
    const nodeFound = nodes[index.column][index.row];

    nodes[index.column][index.row].conn = nodeFound.conn.filter((c) => c.id_nodo !== idToFilter);

    setNodes(nodes);
    handleUpdateWf();
    handleCancel();
  }

  function handleAdd(taskToAdd, parentNodeId) {
    const index = findIndexNodeById(parentNodeId);

    if (parentNodeId === -1 && nodes.length>0){
      taskToAdd.conn = [];
      nodes[0].push(taskToAdd)
    }

    if (!index) nodes.push([taskToAdd]);
    else {
      if (nodes[index.column + 1]) nodes[index.column + 1].push(taskToAdd);
      else nodes[index.column + 1] = [taskToAdd];
    }

    setNodes(nodes);
    handleUpdateWf();
    setCounterNode(counterNode+1);
    handleCancel();
  }

  function handleEdit(taskToEdit, parentNodeId) {
    const index = findIndexNodeById(parentNodeId);

    if (parentNodeId === -1 && nodes.length > 0) {
      taskToEdit.conn = [];
      nodes[0].push(taskToEdit)
    }

    nodes[index.column] = [taskToEdit]

    setNodes(nodes);
    handleUpdateWf();
    handleCancel();
  }

  function handleConnection(task) {
    const index = findIndexNodeById(task.id_nodo);
    nodes[index.column][index.row] = task;
    setNodes(nodes);
    handleUpdateWf();
    handleCancel();
  }

  function handleMoveJob(job, column, pos, new_column, new_pos) {
    // const index = findIndexNodeById(task.id_nodo);
    const wfJob = nodes[column].splice(pos,1)
    if (new_column<0) new_column = 0;
    if (new_pos<0) new_pos = 0;
    nodes[new_column].splice(new_pos, 0, job);
    setNodes(nodes);
    handleUpdateWf();
    handleCancel();
  }

  function handleCancel() {
    setTab('empty');
    setLinkMode(initialState);
  }

  function handleLink(task) {
    const index = findIndexNodeById(task.id_nodo);
    nodes[index.column][index.row] = task;
    setNodes(nodes);
    handleUpdateWf();
    handleCancel();
  }

  function handleAddColumn(index) {
    nodes.splice(index, 0, []);
    setNodes(nodes);
    handleUpdateWf();
  }

  function handleDeleteColumn(index) {
    nodes.splice(index-1, 1);
    setNodes(nodes);
    handleUpdateWf();
  }

  function LegendComponent() {
    const legendLabels = [
      { label: t('common.nodesOption.succed'), color: theme.palette.success.light, type: 'solid' },
      { label: t('common.nodesOption.error'), color: theme.palette.error.light, type: 'solid' },
      { label: t('common.nodesOption.always'), color: theme.palette.info.light, type: 'solid' },
      { label: t('common.nodesOption.condition'), color: theme.palette.secondary.dark, type: 'solid' },
      { label: t('common.nodesOption.noDependency'), color: theme.palette.warning.light, type: 'dashed' }
    ];

    return (
      <Box>
        <Typography variant='h6'>{t('workflowEdit.components.task.legend')}</Typography>

        { legendLabels.map((legend, i) => (
          <Box key={`${legend.label}-${i}`} sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1
            }}
          >
            <Box sx={{
              width: '42px',
              height: '3px',
              border: `2px ${legend.type} ${legend.color}`,
              position: 'relative',
              "&:before": {
                content: '""',
                position: 'absolute',
                zIndex: 2,
                width: '12px',
                height: '12px',
                left: '50%',
                bottom: '50%',
                transform: 'translate(-50%, 50%)',
                backgroundColor: legend.color,
                borderRadius: '100%'
              }
            }}
            />
            <Typography variant="subtitle1">{legend.label}</Typography>
          </Box>
        ))}
      </Box>
    )
  }

  function ZoomComponent() {
    return (
      <Box sx={{ display: 'flex', gap: 1 }}>
        <IconButton aria-label="zoom-out" onClick={handleZoomIn} sx={{
          backgroundColor: theme.palette.common.white,
          borderRadius: '6px',
          boxShadow: theme.shadows[3]
        }}>
          <AddOutlinedIcon />
        </IconButton>
        <IconButton aria-label="zoom-in" onClick={handleZoomOut} sx={{
          backgroundColor: theme.palette.common.white,
          borderRadius: '6px',
          boxShadow: theme.shadows[3]
        }}>
          <RemoveOutlinedIcon />
        </IconButton>

        <Box sx={{
          boxShadow: theme.shadows[3],
          backgroundColor: theme.palette.primary.main,
          borderRadius: '6px',
          display: 'flex',
          px: 1,
          alignItems: 'center'
        }}>
          <Typography
            sx={{ color: theme.palette.common.white }}
            variant="subtitle1"
          >
            {t('workflowEdit.components.task.counter', { tasks: getCounterNode() })}
          </Typography>
        </Box>
      </Box>
    );
  }

  function TabComponent() {
    return (
      <>
        { tab === 'connection' && <ConnectionComponent
          task={taskSelected}
          optionsAction={optionsAction}
          handleCancel={handleCancel}
          handleDelete={handleDeleteConnetion}
          handleConnection={handleConnection}
        />}
        { tab === 'edit' && <EditTaskComponent
          task={taskSelected}
          optionsType={data}
          optionsAction={optionsAction}
          handleCancel={handleCancel}
          handleDelete={handleDelete}
          handleEdit={handleEdit}
          handleAdd={handleAdd}
          counterNode={counterNode}
        />}
        { tab === 'add' && <AddTaskComponent
          task={taskSelected}
          optionsType={data}
          optionsAction={optionsAction}
          handleCancel={handleCancel}
          counterNode={counterNode}
          handleAdd={handleAdd}
        />}
        { tab === 'link' && <LinkComponent
          task={taskSelected}
          handleLink={handleLink}
          optionsAction={optionsAction}
          handleCancel={handleCancel}
          linkMode={linkMode}
          handleLinkMode={setLinkMode}
        />}
      </>
    );
  }

  function ActionsComponent() {
    return (
      <>
        { !readonly &&
          <Box sx={{
            display: 'flex',
            gap: 2,
            mr: tab === 'empty' ? 0 : '340px'
          }}
          >
            <Button variant="outlined" onClick={handleCancelSubmit} size="large">
              {t('workflowEdit.components.task.form.cancel')}
            </Button>
            <Button variant="contained" onClick={() => handleConfirmSubmit(nodes)} size="large">
              {t('workflowEdit.components.task.form.confirm')}
            </Button>
          </Box>
        }
      </>
    );
  }

  function handleAction(event, task, type, index) {

    if (readonly) return;

    if (type === 'add-column') {
      handleAddColumn(index);
      return;
    }

    if (type === 'delete-column') {
      handleDeleteColumn(index);
      return;
    }

    if (task.type === "ST" && type !== "add") return;
    
    if (projectId && mode !== 'ej') getTemplateByProject({ query: { projectId }});
    setTaskSelected(task);
    setTab(type);
  }

  useLayoutEffect(() => {
    createHashMapNodes();
    setCounterNode(getMaxIdNode());
    if (projectId) getTemplateByProject({ query: { projectId }});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <>
      { isLoaded['getTemplateProject'] && false ?
        (
          <Backdrop
            invisible
            sx={{ zIndex: (theme) => theme.zIndex.drawer - 1 }}
            open={true}
          >
            <CircularProgress color="primary" />
          </Backdrop>
        )
        :(
          <>
            <Box sx={{ width: '100%', overflow: 'auto', position: 'absolute', top: '64px', zIndex: 0 }}>
              <Workflow
                wfParam={mode === 'ej' ? wfParam : nodes}
                updateWF={updateWF}
                mode={mode}
                scale={scale}
                handleAction={handleAction}
                linkMode={linkMode}
                setLinkMode={setLinkMode}
                handleMoveJob={handleMoveJob}
              />
            </Box>

            <Box sx={{ position: 'absolute', bottom: '0px', zIndex: 2, display: 'flex', alignItems: 'flex-end', gap: 3, mb: 4 }}>
              <LegendComponent />
              <ZoomComponent />
            </Box>

            <Box sx={{ position: 'absolute', width: '100%', bottom: '0px', zIndex: 1, display: 'flex', justifyContent: 'flex-end', pr: 14, mb: 4 }}>
              <ActionsComponent />
            </Box>

            <Box sx={{
              position: 'absolute',
              right: '0px',
              bottom: '0px',
              minWidth: '240px',
              maxWidth: '340px',
              borderLeft: `1px solid ${theme.palette.grey[400]}`,
              height: '100%',
              zIndex: 3,
              backgroundColor: theme.palette.common.white,
              opacity: tab === 'empty' ? 0 : 1,
              pointerEvents: tab === 'empty' ? 'none' : 'auto',
              transition: '.3s'
            }}
            >
              <TabComponent />
            </Box>
          </>
        )
      }

      <Snackbar
        open={linkMode.active}
        anchorOrigin={{ horizontal: 'center', vertical: 'top' }}
        message={t("workflowEdit.components.task.linkModeMessage")}
        action={
          <Button size="small" onClick={handleCancel}>
            {t('workflowEdit.components.task.form.cancel')}
          </Button>
        }
      />
    </>
  );
};
